
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { addDays, format, subDays } from 'date-fns';
import { PageLayout } from '@/components/layout';
import { mealPlanService } from '@/services/mealPlan';
import { mealNoteService } from '@/services/mealNoteService';
import DateCard from '@/components/meal-planner/DateCard';
import ErrorAlert from '@/components/meal-planner/ErrorAlert';
import LoadingSkeletons from '@/components/meal-planner/LoadingSkeletons';
import NoRecipesMessage from '@/components/meal-planner/NoRecipesMessage';
import { useCurrentUserFamily } from '@/hooks/useCurrentUserFamily';
import { useUser } from '@/contexts/UserContext';
import { useRecipes } from '@/hooks/useRecipes';
import { useMealPlans } from '@/hooks/useMealPlans';
import { useMealCompositions } from '@/hooks/useMealCompositions';
import { useMealNotes } from '@/hooks/useMealNotes';
import { recipeService } from '@/services/recipe';
import { mealCompositionService } from '@/services/mealComposition';
import { MEAL_PLANS, RECIPES, MEAL_COMPOSITIONS } from '@/constants/queryKeys';
import { toast } from 'sonner';
import { MEAL_NOTES } from '@/constants/queryKeys';
import { Share } from 'lucide-react';

import { MealType } from '@/types/mealPlan';
import { useEffect } from 'react';
import { useShare } from '@/hooks/useShare';
import { Button } from '@/components/ui/button';
import { MealInfo } from '@/components/ui/daily-tile';

export default function MealPlanner() {
  const queryClient = useQueryClient();
  const today = new Date();
  const startDate = today; // Start from today
  const endDate = addDays(today, 6); // End 6 days from today (7 days total)
  const sevenDays = Array.from({ length: 7 }, (_, i) => addDays(startDate, i));
  
  const [submittingDates, setSubmittingDates] = useState<Set<string>>(new Set());
  const { shareWeeklyMealPlans, canShare } = useShare();

  
  const { familyId, isLoading: familyLoading } = useCurrentUserFamily();
  const { user } = useUser();
  // Simplified data fetching with standard React Query hooks
  const { data: recipes = [], isLoading: recipesLoading, refetch: refetchRecipes } = useRecipes();
  const { data: compositions = [], isLoading: compositionsLoading, refetch: refetchCompositions } = useMealCompositions();
  const { data: mealPlans = [], isLoading: mealPlansLoading, error, refetch: refetchMealPlans } = useMealPlans(startDate, endDate, familyId);
  const { data: mealNotes = [], isLoading: mealNotesLoading, refetch: refetchMealNotes } = useMealNotes(startDate, endDate, familyId);


  const isLoading = recipesLoading || mealPlansLoading || compositionsLoading || mealNotesLoading;

  // Group meal plans by date
  const mealPlansByDate = mealPlans.reduce((acc, plan) => {
    if (!acc[plan.date]) {
      acc[plan.date] = [];
    }
    acc[plan.date].push({
      ...plan,
      recipe: null,
      mealComposition: null
    });
    return acc;
  }, {} as Record<string, any[]>);

  // Group meal notes by date
  const mealNotesByDate = mealNotes.reduce((acc, note) => {
    if (!acc[note.date]) {
      acc[note.date] = [];
    }
    acc[note.date].push(note);
    return acc;
  }, {} as Record<string, any[]>);

  const saveMealPlanMutation = useMutation({
    mutationFn: (variables: { 
      date: string; 
      recipeId: string | null; 
      compositionId?: string | null;
      familyId: string;
      mealType?: MealType;
    }) => {
      const mealType = variables.mealType || 'dinner'; // Default to dinner for backward compatibility
      if (variables.compositionId) {
        return mealPlanService.saveMealPlanComposition(variables.date, variables.compositionId, variables.familyId, mealType);
      }
      return mealPlanService.saveMealPlan(variables.date, variables.recipeId, variables.familyId, mealType);
    },
    onMutate: async (variables) => {
      setSubmittingDates(prev => new Set(prev).add(variables.date));
      
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['mealPlans'] });
      
      // Snapshot previous value for rollback
      const previousMealPlans = queryClient.getQueryData(['mealPlans', format(startDate, 'yyyy-MM-dd'), format(endDate, 'yyyy-MM-dd'), familyId]);
      
      // Optimistically update cache
      queryClient.setQueryData(['mealPlans', format(startDate, 'yyyy-MM-dd'), format(endDate, 'yyyy-MM-dd'), familyId], (old: any[] = []) => {
        const mealType = variables.mealType || 'dinner';
        
        // Create optimistic meal plan
        const optimisticMealPlan = {
          id: `temp-${Date.now()}`, // Temporary ID
          date: variables.date,
          recipeId: variables.recipeId,
          mealCompositionId: variables.compositionId || null,
          mealType,
          userId: user?.id,
          familyId: variables.familyId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          recipe: null,
          mealComposition: null
        };
        
        return [...old, optimisticMealPlan];
      });
      
      // Show immediate success feedback
      const itemName = variables.recipeId 
        ? recipes.find(r => r.id === variables.recipeId)?.title 
        : compositions.find(c => c.id === variables.compositionId)?.name;
      
      toast.success(`${itemName} added to ${variables.mealType || 'dinner'} plan`);
      
      return { previousMealPlans, variables };
    },
    onError: (error: Error, variables, context) => {
      // Rollback optimistic update
      if (context?.previousMealPlans) {
        queryClient.setQueryData(['mealPlans', format(startDate, 'yyyy-MM-dd'), format(endDate, 'yyyy-MM-dd'), familyId], context.previousMealPlans);
      }
      
      toast.error(`Failed to save meal plan: ${error.message}`);
    },
    onSuccess: () => {
      // Still invalidate to ensure server state sync
      queryClient.invalidateQueries({ queryKey: ['mealPlans'], type: 'all' });
    },
    onSettled: (data, error, variables) => {
      setSubmittingDates(prev => {
        const next = new Set(prev);
        next.delete(variables.date);
        return next;
      });
    },
  });

  const deleteMealPlanMutation = useMutation({
    mutationFn: mealPlanService.deleteMealPlan,
    onMutate: async (mealPlanId: string) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['mealPlans'] });
      
      // Snapshot previous value for rollback
      const previousMealPlans = queryClient.getQueryData(['mealPlans', format(startDate, 'yyyy-MM-dd'), format(endDate, 'yyyy-MM-dd'), familyId]);
      
      // Optimistically remove from cache
      queryClient.setQueryData(['mealPlans', format(startDate, 'yyyy-MM-dd'), format(endDate, 'yyyy-MM-dd'), familyId], (old: any[] = []) => {
        return old.filter(plan => plan.id !== mealPlanId);
      });
      
      toast.success('Meal removed successfully');
      
      return { previousMealPlans };
    },
    onError: (error: Error, mealPlanId, context) => {
      // Rollback optimistic update
      if (context?.previousMealPlans) {
        queryClient.setQueryData(['mealPlans', format(startDate, 'yyyy-MM-dd'), format(endDate, 'yyyy-MM-dd'), familyId], context.previousMealPlans);
      }
      
      toast.error(`Failed to remove meal: ${error.message}`);
    },
    onSuccess: () => {
      // Still invalidate to ensure server state sync
      queryClient.invalidateQueries({ queryKey: ['mealPlans'], type: 'all' });
    },
  });

  const saveMealNoteMutation = useMutation({
    mutationFn: (variables: { date: string; noteText: string; mealType: 'lunch' | 'dinner'; familyId: string; userId: string }) => {
      return mealNoteService.createMealNote(variables);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mealNotes'], type: 'all' });
      toast.success('Meal note saved successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to save meal note: ${error.message}`);
    },
  });

  const deleteMealNoteMutation = useMutation({
    mutationFn: mealNoteService.deleteMealNote,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mealNotes'], type: 'all' });
      toast.success('Meal note removed successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to remove meal note: ${error.message}`);
    },
  });

  const handleSaveMealPlan = async (date: Date, recipeId: string | null, compositionId?: string | null, mealType: MealType = 'dinner') => {
    if (!familyId) {
      toast.error("Could not determine family. Please try again later.");
      return;
    }

    const dateString = format(date, 'yyyy-MM-dd');
    await saveMealPlanMutation.mutateAsync({ 
      date: dateString, 
      recipeId, 
      compositionId,
      familyId,
      mealType
    });
  };

  const handleRemoveMealPlan = async (mealPlanId: string) => {
    await deleteMealPlanMutation.mutateAsync(mealPlanId);
  };

  const handleSaveMealNote = async (date: Date, noteText: string, mealType: 'lunch' | 'dinner') => {
    if (!familyId) {
      toast.error("Could not determine family. Please try again later.");
      return;
    }

    if (!user) {
      toast.error("User not authenticated. Please sign in again.");
      return;
    }

    const dateString = format(date, 'yyyy-MM-dd');
    await saveMealNoteMutation.mutateAsync({
      date: dateString,
      noteText,
      mealType,
      familyId,
      userId: user.id
    });
  };

  const handleRemoveMealNote = async (mealNoteId: string) => {
    await deleteMealNoteMutation.mutateAsync(mealNoteId);
  };

  // Create weekly meal plan data for sharing
  const getWeeklyMealPlans = () => {
    const weeklyData: { date: Date; meals: MealInfo[] }[] = [];
    
    sevenDays.forEach(date => {
      const dateString = format(date, 'yyyy-MM-dd');
      const dailyMealPlans = mealPlansByDate[dateString] || [];
      const dailyMealNotes = mealNotesByDate[dateString] || [];
      
      // Convert to MealInfo format
      const mealPlanMeals: MealInfo[] = dailyMealPlans.map(mealPlan => {
        if (mealPlan.recipeId) {
          const recipe = recipes.find(r => r.id === mealPlan.recipeId);
          if (recipe) {
            return {
              type: 'recipe' as const,
              id: recipe.id,
              name: recipe.title,
              mealType: mealPlan.mealType,
              mealPlanId: mealPlan.id,
            };
          }
        }
        
        if (mealPlan.mealCompositionId) {
          const composition = compositions.find(c => c.id === mealPlan.mealCompositionId);
          if (composition) {
            return {
              type: 'composition' as const,
              id: composition.id,
              name: composition.name,
              mealType: mealPlan.mealType,
              mealPlanId: mealPlan.id,
            };
          }
        }
        
        return null;
      }).filter(Boolean) as MealInfo[];

      const mealNoteMeals: MealInfo[] = dailyMealNotes.map(mealNote => ({
        type: 'note' as const,
        id: mealNote.id,
        name: mealNote.noteText,
        mealType: mealNote.mealType as 'lunch' | 'dinner',
        mealNoteId: mealNote.id,
      }));

      const allMeals = [...mealPlanMeals, ...mealNoteMeals];
      weeklyData.push({ date, meals: allMeals });
    });
    
    return weeklyData;
  };

  const handleShareWeeklyPlan = () => {
    const weeklyMealPlans = getWeeklyMealPlans();
    // Only include today and future dates
    const upcomingMealPlans = weeklyMealPlans.filter(day => day.date >= today);
    shareWeeklyMealPlans(upcomingMealPlans);
  };

  // Check if there are any meal plans for today or future dates
  const hasUpcomingMeals = () => {
    const weeklyMealPlans = getWeeklyMealPlans();
    return weeklyMealPlans.some(day => 
      day.date >= today && day.meals.filter(meal => meal.type !== 'note').length > 0
    );
  };

  const shouldShowShareButton = canShare && hasUpcomingMeals();

  return (
    <PageLayout
      title="Meal Planner"
      description="Plan your meals for the week. Select multiple recipes for each day and build your shopping list."
    >
        {/* Share Button above date tiles - iOS only */}
        {shouldShowShareButton && (
          <div className="mt-8 mb-6 px-6">
            <Button
              onClick={handleShareWeeklyPlan}
              className="w-full flex items-center justify-center gap-2 py-3"
              variant="secondary"
            >
              <Share className="h-4 w-4" />
              Share this week's menu
            </Button>
          </div>
        )}

        {error && (
          <ErrorAlert 
            title="Error Loading Meal Plans" 
            description={(error as Error).message}
          />
        )}

        {isLoading || familyLoading ? (
          <LoadingSkeletons />
        ) : (recipes?.length === 0 && compositions?.length === 0) ? (
          <NoRecipesMessage />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-8">
            {sevenDays.map((date) => {
              const dateString = format(date, 'yyyy-MM-dd');
              const dailyMealPlans = mealPlansByDate[dateString] || [];
              const dailyMealNotes = mealNotesByDate[dateString] || [];
              
              return (
                <DateCard
                  key={date.toISOString()}
                  date={date}
                  mealPlans={dailyMealPlans}
                  mealNotes={dailyMealNotes}
                  recipes={recipes || []}
                  compositions={compositions || []}
                  isLoading={isLoading}
                  isSubmitting={submittingDates.has(dateString)}
                  hasError={Boolean(error)}
                  today={today}
                  onSaveMealPlan={handleSaveMealPlan}
                  onRemoveMealPlan={handleRemoveMealPlan}
                  onSaveMealNote={handleSaveMealNote}
                  onRemoveMealNote={handleRemoveMealNote}
                />
              );
            })}
          </div>
        )}
    </PageLayout>
  );
}
